// src/components/PurchaseFlow/Steps/CustomerHandoffStep.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserCog, 
  faArrowRight,
  faLink,
  faCopy,
  faRefresh,
  faCheck,
  faClock,
  faSpinner,
  faExclamationCircle,
  faShuffle
} from '@fortawesome/free-solid-svg-icons';
import QRCode from 'qrcode';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import api from '../../../../services/api';
import { Button, MessageBlock } from '../../../ui';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { withoutGeoLocationStepIfCash } from '../../../../config/policyProducts';
import './CustomerHandoffStep.css';

const MySwal = withReactContent(Swal);

const parseFlowSteps = (value) => {
  const normalizedValue = value?.trim();

  if (!normalizedValue || normalizedValue.toLowerCase() === 'false') {
    return [];
  }

  return normalizedValue.split(',').map(step => step.trim()).filter(Boolean);
};

const CustomerHandoffStep = ({ onNext, onClose, onFlowTerminalStatus, clientData, purchaseRequestId, formData }) => {

  // Configuración desde variables de entorno
  const ALLOW_PROCESSED_STEPS = process.env.REACT_APP_CUSTOMER_ALLOW_PROCESSED_STEPS;
  const ALLOWED_PROCESSED_STEPS_ARRAY = ALLOW_PROCESSED_STEPS 
    ? ALLOW_PROCESSED_STEPS.split(',').map(step => step.trim())
    : [];

  const FLOW_STEPS = parseFlowSteps(process.env.REACT_APP_CUSTOMER_FLOW_STEPS);
  const FLOW_STEPS_ALTERNATIVE = parseFlowSteps(
    process.env.REACT_APP_CUSTOMER_FLOW_STEPS_ALTERNATIVE
  );
  const canShowAlternativeVerification = FLOW_STEPS_ALTERNATIVE.length > 0;

  // Configuración de tiempos (en segundos)
  const ALTERNATIVE_VERIFICATION_DELAY_SECONDS = 5; // 3 minutos

  // Estados
  const [customerUrl, setCustomerUrl] = useState('');
  const [flowId, setFlowId] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [customerSteps, setCustomerSteps] = useState([]);
  const [stepStatuses, setStepStatuses] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Estados para consultas manuales
  const [manualQueryCooldown, setManualQueryCooldown] = useState(0);
  const [canManualQuery, setCanManualQuery] = useState(true);
  
  // Constantes para tipos de flujo
  const FLOW_TYPE_STANDARD = 'standard';
  const FLOW_TYPE_NO_BIOMETRIC = 'no_biometric';

  // Estados de control
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [isLoadingToken, setIsLoadingToken] = useState(false);
  const [isFlowCompleted, setIsFlowCompleted] = useState(false);
  const [isAlternativeVerificationEnabled, setIsAlternativeVerificationEnabled] = useState(false);
  const [flowType, setFlowType] = useState(FLOW_TYPE_STANDARD);
  const [locationValidationFailed, setLocationValidationFailed] = useState(false);
  
  // Función para verificar el progreso del cliente
  const checkCustomerProgress = async () => {
    try {
      const response = await api.get(
        `/customers/${clientData.id}/purchase-requests/${purchaseRequestId}/self-service/status`
      );
      
      const {
        status,
        steps,
        expires_at,
        flow_config,
        flow_type,
        enforce_on_site_validation: enforceOnSiteValidation,
        location_validation_failed: locationFailed,
      } = response.data.data;

      const hasLocationFailure = Boolean(
        enforceOnSiteValidation && locationFailed
      );
      
      // Sincronizar flow_type si viene en respuesta
      if (typeof flow_type !== 'undefined') {
        setFlowType(flow_type || FLOW_TYPE_STANDARD);
      }
      
      if (expires_at) {
        const expiresDate = new Date(expires_at);
        const now = new Date();
        const remaining = Math.max(0, Math.floor((expiresDate - now) / 1000));
        setTimeRemaining(remaining);
        
        // Resetear el estado de expiración si el token aún es válido
        if (remaining > 0) {
          setIsTokenExpired(false);
        } else {
          setIsTokenExpired(true);
        }
      }
      
      // Configurar los pasos desde la respuesta de la API
      if (flow_config && flow_config.length > 0) {
        setCustomerSteps(flow_config);
      }
      
      if (steps) {
        setStepStatuses(steps);
      }

      setLocationValidationFailed(hasLocationFailure);
      
      // Si el flujo está completado, avanzar al siguiente paso sin mostrar pantalla de confirmación
      if (status === 'completed') {
        onNext({});
        return { hasLocationFailure };
      }

      return { hasLocationFailure };
      
    } catch (error) {
      console.error('Error verificando progreso del cliente:', error);
      return { hasLocationFailure: false };
    }
  };

  // Función para generar token temporal (refactorizada para aceptar pasos como parámetro)
  const generateTemporaryToken = async (flowSteps, flowTypeValue = null) => {
    setIsLoadingToken(true);
    try {
      // Usar el valor pasado como parámetro si existe, sino usar el estado
      const flowTypeToSend = flowTypeValue !== null ? flowTypeValue : flowType;
      
      // Los pasos se configuran dinámicamente desde la respuesta de la API
      const response = await api.post(`/customers/${clientData.id}/purchase-requests/${purchaseRequestId}/self-service/init`, {
        flow_steps: flowSteps,
        flow_type: flowTypeToSend
      });
      
      const { flow_id, customer_token, expires_at, flow_type } = response.data.data;
      setFlowId(flow_id);
      
      // Sincronizar flow_type desde el backend
      if (typeof flow_type !== 'undefined') {
        setFlowType(flow_type || FLOW_TYPE_STANDARD);
      }
      
      // Construir URL del cliente usando el token
      const baseUrl = process.env.REACT_APP_CUSTOMER_BASE_URL || window.location.origin;
      const customerFlowUrl = `${baseUrl}/m/onboarding/${customer_token}`;
      setCustomerUrl(customerFlowUrl);
      
      // Calcular tiempo restante
      if (expires_at) {
        const expiresDate = new Date(expires_at);
        const now = new Date();
        const remaining = Math.max(0, Math.floor((expiresDate - now) / 1000));
        setTimeRemaining(remaining);
      }
      
      // Resetear estado de expiración
      setIsTokenExpired(false);
      
      // Resetear el cooldown manual para permitir consultas inmediatas
      setManualQueryCooldown(0);
      setCanManualQuery(true);
      
      return { success: true };
    } catch (error) {
      console.error('Error al generar token temporal:', error);
      return { success: false, error };
    } finally {
      setIsLoadingToken(false);
    }
  };

  // Generar token temporal y URL para QR (inicialización)
  useEffect(() => {
    // Solo generar token si el flujo no está completado
    if (clientData?.id && purchaseRequestId && !isFlowCompleted) {
      generateTemporaryToken(withoutGeoLocationStepIfCash(FLOW_STEPS, formData?.policyProductId)).then((result) => {
        if (!result.success) {
          MySwal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo iniciar el proceso de autogestión'
          });
          // Asegurar que flowType esté en standard si falla
          setFlowType(FLOW_TYPE_STANDARD);
        }
        // Si tiene éxito, flow_type ya fue sincronizado dentro de generateTemporaryToken
      });
    }
  }, [clientData?.id, purchaseRequestId, isFlowCompleted]);

  // Verificar si el token ya está expirado al montar el componente
  useEffect(() => {
    if (timeRemaining <= 0 && !isLoadingToken) {
      setIsTokenExpired(true);
    }
  }, [timeRemaining, isLoadingToken]);

  // Timer para el countdown
  useEffect(() => {
    // No ejecutar el timer si el flujo está completado
    if (isFlowCompleted || timeRemaining <= 0) {
      // Si no hay tiempo restante, marcar como expirado inmediatamente
      if (timeRemaining <= 0) {
        setIsTokenExpired(true);
      }
      return;
    }
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsTokenExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, isFlowCompleted]);

  // Timer para el cooldown de consultas manuales
  useEffect(() => {
    if (manualQueryCooldown <= 0) {
      setCanManualQuery(true);
      return;
    }
    
    const timer = setInterval(() => {
      setManualQueryCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanManualQuery(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [manualQueryCooldown]);

  // Generar QR cuando el canvas esté disponible
  useEffect(() => {
    if (!customerUrl || isLoadingToken) {
      return undefined;
    }

    let cancelled = false;
    let retryId;
    let attempts = 0;
    const maxAttempts = 40;

    const generateQRWhenReady = () => {
      if (cancelled) {
        return;
      }

      const canvas = document.getElementById('customer-qr-canvas');
      if (canvas) {
        generateQRCode(customerUrl);
        return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        return;
      }

      retryId = setTimeout(generateQRWhenReady, 50);
    };

    generateQRWhenReady();

    return () => {
      cancelled = true;
      if (retryId) {
        clearTimeout(retryId);
      }
    };
  }, [customerUrl, isLoadingToken]);

  const generateQRCode = async (url) => {
    try {
      const canvas = document.getElementById('customer-qr-canvas');
      if (canvas) {
        await QRCode.toCanvas(canvas, url, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        });
      }
    } catch (error) {
      console.error('Error generando QR:', error);
    }
  };

  // Función para regenerar el token temporal
  const handleRegenerateToken = async () => {
    // No permitir regenerar token si el flujo está completado
    if (isFlowCompleted) {
        MySwal.fire({
          icon: 'info',
          title: 'Proceso Completado',
          text: 'El proceso ya está completado. No se requiere regenerar el token.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
      return;
    }

    try {
      setIsRefreshing(true);
      
      const response = await api.post(
        `/customers/${clientData.id}/purchase-requests/${purchaseRequestId}/self-service/regenerate-token`
      );
      
      if (response.data.success) {
        const { customer_token, expires_at } = response.data.data;
        
        // Actualizar la URL del cliente
        const baseUrl = window.location.origin;
        const newCustomerFlowUrl = `${baseUrl}/m/onboarding/${customer_token}`;
        setCustomerUrl(newCustomerFlowUrl);
        
        // Actualizar el tiempo restante
        if (expires_at) {
          const expiresDate = new Date(expires_at);
          const now = new Date();
          const remaining = Math.max(0, Math.floor((expiresDate - now) / 1000));
          setTimeRemaining(remaining);
        }
        
        // Resetear estado de expiración
        setIsTokenExpired(false);
        
        // Hacer una nueva consulta del estado
        checkCustomerProgress();
        
        // Resetear el cooldown manual para permitir consultas inmediatas
        setManualQueryCooldown(0);
        setCanManualQuery(true);
        
        MySwal.fire({
          icon: 'success',
          title: 'Token regenerado',
          text: 'Se ha generado un nuevo QR para el cliente',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
      }
      
    } catch (error) {
      console.error('Error al regenerar token:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo regenerar el token temporal',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Consulta inicial del estado del cliente
  useEffect(() => {
    // Solo hacer consulta inicial si el flujo no está completado
    if (flowId && !isFlowCompleted) {
      // Consulta inicial inmediata
      checkCustomerProgress();
    }
  }, [flowId, purchaseRequestId, clientData?.id, isFlowCompleted]);

  // Habilitar botón de verificación alternativa después del tiempo configurado
  useEffect(() => {
    if (!canShowAlternativeVerification) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setIsAlternativeVerificationEnabled(true);
    }, ALTERNATIVE_VERIFICATION_DELAY_SECONDS * 1000); // Convertir segundos a milisegundos

    return () => clearTimeout(timer);
  }, [canShowAlternativeVerification]);

  // Mapeo de pasos a etiquetas legibles (igual que en CustomerFlow.js)
  const stepLabelMap = {
    'ValidaStep': 'Identidad',
    'PaymentStep': 'Medio de Pago',
    'ConfirmationStep': 'Contrato',
    'DocumentUploadStep': 'Documentación',
    'PhoneValidationStep': 'Validación de Teléfono',
    'PreSignatureConfirmationStep': 'Confirmación y firma',
    'GeoLocationValidationStep': 'Confirmación de ubicación'
  };

  const getStepLabel = (stepName) => {
    return stepLabelMap[stepName] || stepName;
  };

  const getStepStatusMessage = (status, stepName) => {
    switch (status) {
      case 'completed':
        return '';
      case 'in_progress':
        return 'En progreso...';
      case 'error':
        if (stepName === 'GeoLocationValidationStep' && locationValidationFailed) {
          return 'No pudo validar ubicación';
        }
        return 'Error - Requiere atención';
      case 'processed':
        // Si el paso procesado está permitido, mostrarlo como completado
        if (stepName && isProcessedStepAllowed(stepName)) {
          return '';
        }
        return 'Procesado - Esperando Confirmación';
      default:
        return 'Pendiente';
    }
  };

  const handleStepInfo = (stepName, status) => {
    if (status === 'processed') {
      // Si el paso procesado está permitido, mostrar mensaje diferente
      if (isProcessedStepAllowed(stepName)) {
        MySwal.fire({
          icon: 'success',
          title: `Paso Completado`,
          text: 'Este paso ha sido completado por el cliente y está marcado como válido para continuar.',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#28a745'
        });
      } else {
        MySwal.fire({
          icon: 'info',
          title: `Importante`,
          html: `<div>
                   <div>Este paso fue procesado correctamente y está pendiente de completarse por parte del sistema.</div>
                   <hr>
                   <div style="font-size: 0.80em; color: #666; margin-top: 6px;">Esta validación puede demorar unos segundos.</div>
                 </div>`,
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        });
      }
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(customerUrl);
      MySwal.fire({ 
        icon: 'success',
        title: 'Enlace copiado',
        text: 'El enlace ha sido copiado al portapapeles',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000
      });
    } catch (err) {
      console.error('Error al copiar:', err);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo copiar el enlace'
      });
    }
  };

  const handleWhatsAppShare = () => {
    if (!customerUrl) return;

    const clientName = clientData?.firstName || clientData?.name || 'Cliente';
    // Obtener teléfono desde formData (E.164); wa.me requiere dígitos sin +
    const rawPhone = formData?.contactPhone;
    const phone = rawPhone ? rawPhone.replace(/\D/g, '') : '';
    
    const whatsappText = encodeURIComponent(
      `Hola ${clientName}, completa tu proceso de préstamo desde tu celular: ${customerUrl}`
    );
    const whatsappUrl = phone ? 
      `https://wa.me/${phone}?text=${whatsappText}` : 
      `https://wa.me/?text=${whatsappText}`;
    
    window.open(whatsappUrl, '_blank');
  };

  const handleRefreshStatus = async () => {
    // No permitir consultas si el flujo está completado
    if (isFlowCompleted) {
      MySwal.fire({
        icon: 'info',
        title: 'Proceso Completado',
        text: 'El proceso ya está completado. No se requieren más consultas.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
      return;
    }

    // Verificar si puede hacer consulta manual (cooldown de 10 segundos)
    if (!canManualQuery) {
      MySwal.fire({
        icon: 'warning',
        title: 'Espera un momento',
        text: `Debes esperar ${formatTime(manualQueryCooldown)} para la próxima consulta manual`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
      return;
    }

    setIsRefreshing(true);
    setCanManualQuery(false);
    
    try {
      const progress = await checkCustomerProgress();
      
      // Configurar el cooldown de 10 segundos para la próxima consulta manual
      setManualQueryCooldown(10);
      
      const hasLocationFailure = progress?.hasLocationFailure;

      MySwal.fire({
        icon: hasLocationFailure ? 'warning' : 'success',
        title: hasLocationFailure ? 'Validación de ubicación' : 'Estado actualizado',
        text: hasLocationFailure
          ? 'El cliente no pudo completar la validación de ubicación.'
          : 'Se ha consultado el estado del cliente',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: hasLocationFailure ? 4000 : 2000
      });
      
    } catch (error) {
      console.error('Error al refrescar estado:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar el estado del cliente',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDiscardSale = () => {
    if (window.confirm('¿Estás seguro de que deseas anular esta venta?')) {
      // Lógica de anulación
    }
  };

  const handleAlternativeVerification = async () => {
    // No permitir verificación alternativa si el flujo está completado
    if (isFlowCompleted) {
      MySwal.fire({
        icon: 'info',
        title: 'Proceso Completado',
        text: 'El proceso ya está completado. No se requiere verificación alternativa.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
      return;
    }

    if (!canShowAlternativeVerification) {
      return;
    }

    // Confirmar con el usuario antes de cambiar a verificación alternativa
    const result = await MySwal.fire({
      icon: 'question',
      title: '¿Deseás utilizar la verificación alternativa?',
      html: `
        <div>
          <div>Se generará un nuevo código QR con un flujo de verificación alternativo.</div>
          <div style="margin-top:8px; color:#ab7100;">
            Tené en cuenta que este proceso requiere aprobación manual de un analista para poder finalizar la venta.
          </div>
          <small style="display:block; margin-top:8px;" class="text-muted">
            Usala solo si no es posible completar la verificación estándar.
          </small>
        </div>
      `,
      showCancelButton: true,
      reverseButtons: true,
      cancelButtonText: 'No, cancelar',
      confirmButtonText: 'Sí, continuar',
      customClass: {
        popup: 'swal2-popup-custom',
        confirmButton: 'btn btn-primary',
        cancelButton: 'btn btn-secondary',
      }
    });

    if (!result.isConfirmed) {
      return;
    }

    setIsRefreshing(true);
    
    try {
      // Pasar flow_type='no_biometric' directamente como parámetro para evitar problemas de timing
      const result = await generateTemporaryToken(
        withoutGeoLocationStepIfCash(FLOW_STEPS_ALTERNATIVE, formData?.policyProductId),
        FLOW_TYPE_NO_BIOMETRIC
      );
      
      if (result.success) {
        // El estado flow_type ya fue sincronizado en generateTemporaryToken
        // desde la respuesta del backend, no es necesario setearlo aquí
        
        MySwal.fire({
          icon: 'success',
          title: 'Verificación alternativa iniciada',
          text: 'Se ha generado un nuevo QR con pasos alternativos de verificación.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
      } else {
        // En caso de error, resetear a standard
        setFlowType(FLOW_TYPE_STANDARD);
        throw result.error || new Error('Error desconocido');
      }
    } catch (error) {
      console.error('Error al iniciar verificación alternativa:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo iniciar la verificación alternativa. Por favor, intenta nuevamente.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleContinue = () => {
    onNext({});
  };

  const isProcessedStepAllowed = (stepName) => {
    return ALLOWED_PROCESSED_STEPS_ARRAY.includes(stepName);
  };

  const getCompletedStepsCount = () => {
    return Object.entries(stepStatuses).filter(([step, status]) => {
      return status === 'completed' || (status === 'processed' && isProcessedStepAllowed(step));
    }).length;
  };

  useEffect(() => {
    if (locationValidationFailed) {
      onFlowTerminalStatus?.('error');
    }
  }, [locationValidationFailed, onFlowTerminalStatus]);

  const canContinueWithProcessedSteps = () => {
    if (locationValidationFailed) {
      return false;
    }

    const allStepsCompleted = customerSteps.every(step => {
      const status = stepStatuses[step];
      return status === 'completed' || (status === 'processed' && isProcessedStepAllowed(step));
    });
    
    return allStepsCompleted;
  };

  if (locationValidationFailed) {
    return (
      <div className="rg-sale-handoff-step rg-sale-handoff-step--terminal-error">
        <MessageBlock
          tone="warning"
          title="No se pudo validar la ubicación"
          className="rg-sale-handoff-step__terminal-error"
          actions={(
            <Button variant="outlinePrimary" size="sm" onClick={onClose}>
              Iniciar nueva venta
            </Button>
          )}
        >
          No fue posible completar la validación de ubicación requerida para continuar con el proceso de venta.
          Si el cliente se encuentra presente en el local, puede iniciarse una nueva venta.
        </MessageBlock>
      </div>
    );
  }

  return (
    <div className="container rg-sale-handoff-step">
      <div className="row justify-content-center">
        <div className="col-lg-12">
          {/* Header */}
          <div className="text-center mb-4">
            <h2 className="rg-sale-step__title">Autogestión del Cliente</h2>
            <p className="lead text-muted rg-sale-step__subtitle">
            El cliente debe completar estos pasos desde su celular
            </p>
          </div>

                    <div className="row">
            {/* Columna izquierda - QR y controles */}
            {!isFlowCompleted && (
              <div className="col-lg-6">
                {/* QR Code Section */}
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body text-center p-4">
                    {isLoadingToken ? (
                      <div>
                        <div className="mb-3">
                          <Skeleton 
                            width={200} 
                            height={200} 
                            className="mx-auto"
                            style={{ borderRadius: '8px' }}
                          />
                        </div>
                        
                        <div className="mb-3">
                          <Skeleton width={180} height={16} className="mx-auto" />
                        </div>
                        
                        <div className="mb-3">
                          <Skeleton width={150} height={20} className="mx-auto" />
                        </div>
                        
                        <div className="mb-3">
                          <Skeleton width={220} height={16} className="mx-auto" />
                        </div>
                        
                        <div className="mb-1">
                          <Skeleton height={38} className="w-100" />
                        </div>
                      </div>
                    ) : (
                      <>
                        {isTokenExpired ? (
                          <div className="qr-container mb-1 p-3 border rounded d-flex align-items-center justify-content-center">
                            <div className="text-center d-flex flex-column align-items-center justify-content-center">
                              <div className="mb-3 rg-sale-handoff-step__icon-lg">
                                <FontAwesomeIcon icon={faClock} className="rg-sale-handoff-step__icon-warning" aria-hidden />
                              </div>
                              <p className="text-muted small mb-3 text-center">
                                El enlace ha expirado
                              </p>
                              <Button
                                variant="outlineWarning"
                                size="sm"
                                onClick={handleRegenerateToken}
                                disabled={isRefreshing}
                              >
                                <FontAwesomeIcon
                                  icon={faRefresh}
                                  className={isRefreshing ? 'fa-spin me-2' : 'me-2'}
                                />
                                {isRefreshing ? 'Regenerando...' : 'Regenerar QR'}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="qr-container mb-1">
                              <canvas
                                id="customer-qr-canvas"
                                className="rg-sale-handoff-step__qr-canvas border rounded"
                                aria-hidden
                              />
                            </div>
                            {timeRemaining > 0 && (
                              <div className="rg-sale-handoff-step__expiry d-flex align-items-center justify-content-center mb-3 p-0">
                                <FontAwesomeIcon icon={faClock} className="me-2" aria-hidden />
                                <span className="fw-bold">
                                  El link expira en {formatTime(timeRemaining)}
                                </span>
                              </div>
                            )}
                          </>
                        )}

                        <div className="input-group mb-1">
                          <span className="input-group-text">
                            <FontAwesomeIcon icon={faLink} />
                          </span>
                          <input
                            type="text"
                            className={`form-control form-control-sm ${isTokenExpired ? 'rg-sale-handoff-step__input--disabled' : ''}`}
                            value={isTokenExpired ? '' : customerUrl}
                            readOnly
                            disabled={isTokenExpired}
                            placeholder={isTokenExpired ? '' : ''}
                          />
                          <Button
                            variant="outlineSecondary"
                            type="button"
                            className="rg-sale-handoff-step__icon-btn"
                            onClick={handleCopyLink}
                            disabled={isTokenExpired}
                            aria-label="Copiar enlace"
                          >
                            <FontAwesomeIcon icon={faCopy} aria-hidden />
                          </Button>
                          <Button
                            variant="success"
                            type="button"
                            className="rg-sale-handoff-step__icon-btn"
                            onClick={handleWhatsAppShare}
                            disabled={isTokenExpired}
                            aria-label="Compartir por WhatsApp"
                          >
                            <i className="bi bi-whatsapp" aria-hidden />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Columna derecha - Progreso del cliente */}
            <div className={isFlowCompleted ? "col-12" : "col-lg-6"}>
              {/* Customer Progress */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                  {isLoadingToken ? (
                    <div className="customer-progress">
                      {[1, 2, 3].map((index) => (
                        <div key={index} className="d-flex align-items-center mb-3">
                          <div className="flex-shrink-0 me-3">
                            <Skeleton 
                              width={40} 
                              height={40} 
                              circle={true}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <Skeleton width={120} height={20} className="mb-2" />
                            <Skeleton width={100} height={16} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : isFlowCompleted ? (
                    <div className="text-center py-4">
                      <div className="mb-3 rg-sale-handoff-step__icon-lg">
                        <FontAwesomeIcon icon={faCheck} className="rg-sale-handoff-step__icon-success" aria-hidden />
                      </div>
                      <h5 className="rg-sale-step__title rg-sale-step__title--success rg-sale-handoff-step__title-success">¡Proceso Completado!</h5>
                      <p className="text-muted mb-0">
                        El cliente ha completado todos los pasos exitosamente.
                        Ya puedes continuar con el proceso.
                      </p>
                    </div>
                  ) : (
                    <div className="customer-progress">
                      {customerSteps.map((step, index) => {
                        const status = stepStatuses[step] || 'pending';
                        const stepStatusMessage = getStepStatusMessage(status, step);
                        const isCompleted = status === 'completed';
                        const isInProgress = status === 'in_progress';
                        const hasError = status === 'error';
                        const isProcessed = status === 'processed';
                        
                        const isProcessedAllowed = isProcessed && isProcessedStepAllowed(step);
                        const shouldShowAsCompleted = isCompleted || isProcessedAllowed;
                        
                        return (
                          <div key={step} className="d-flex align-items-center mb-3">
                            <div className="flex-shrink-0 me-3">
                              <div
                                className={`rg-sale-handoff-step__step-index rounded-circle d-flex align-items-center justify-content-center ${
                                  shouldShowAsCompleted ? 'rg-sale-handoff-step__step-index--completed' :
                                  isInProgress ? 'rg-sale-handoff-step__step-index--in-progress' :
                                  hasError ? 'rg-sale-handoff-step__step-index--error' :
                                  isProcessed ? 'rg-sale-handoff-step__step-index--processed' : 'rg-sale-handoff-step__step-index--pending'
                                }`}
                              >
                                {shouldShowAsCompleted ? (
                                  <FontAwesomeIcon icon={faCheck} className="text-white" />
                                ) : isInProgress ? (
                                  <FontAwesomeIcon icon={faSpinner} className="text-white fa-spin" />
                                ) : hasError ? (
                                  <FontAwesomeIcon icon={faExclamationCircle} className="text-white" />
                                ) : isProcessed ? (
                                  <FontAwesomeIcon icon={faClock} className="text-white" />
                                ) : (
                                  <span className="text-white fw-bold">{index + 1}</span>
                                )}
                              </div>
                            </div>
                            <div 
                              className={`flex-grow-1 ${isProcessed ? 'step-processed-clickable' : ''}`}
                              onClick={isProcessed ? () => handleStepInfo(step, status) : undefined}
                              style={isProcessed ? { cursor: 'pointer' } : {}}
                            >
                              <h6 className={stepStatusMessage ? 'mb-1' : 'mb-0'}>{getStepLabel(step)}</h6>
                              {stepStatusMessage && (
                                <div className="d-flex align-items-center">
                                  <p className="mb-0 small text-muted me-2">
                                    {stepStatusMessage}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {isLoadingToken ? (
                    <div className="mt-4">
                      <Skeleton height={8} className="mb-2" />
                      <Skeleton width={150} height={16} className="mx-auto" />
                    </div>
                  ) : (
                    <div className="mt-4">
                      <div className="rg-sale-handoff-step__progress progress mb-2">
                        <div 
                          className="progress-bar" 
                          style={{ 
                            width: `${(getCompletedStepsCount() / customerSteps.length) * 100}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  {isLoadingToken ? (
                    <div className="mt-2 p-1">
                      <div className="d-grid gap-2">
                        <Skeleton height={38} />
                        <Skeleton height={38} />
                        <Skeleton height={38} />
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 p-1">
                      <div className={isFlowCompleted ? "d-flex gap-2" : "d-grid gap-2"}>
                        {/* <button 
                          type="button" 
                          className={`btn btn-outline-danger ${isFlowCompleted ? "flex-fill" : ""}`}
                          onClick={handleDiscardSale}
                          disabled={true}
                        >
                          <FontAwesomeIcon icon={faTimes} className="me-2" />
                          Descartar venta
                        </button> */}
                        {!isFlowCompleted && (
                          <Button
                            type="button"
                            variant="outlinePrimary"
                            onClick={handleRefreshStatus}
                            disabled={isRefreshing || !canManualQuery || isTokenExpired}
                          >
                            <FontAwesomeIcon icon={faRefresh} className="me-2" />
                            {canManualQuery ? 'Consultar Estado' : `Consultar en ${formatTime(manualQueryCooldown)}`}
                          </Button>
                        )}
                        {canShowAlternativeVerification && (
                          <Button
                            type="button"
                            variant="outlineSecondary"
                            onClick={handleAlternativeVerification}
                            hidden={isFlowCompleted}
                            disabled={isRefreshing || isTokenExpired || !isAlternativeVerificationEnabled}
                            title="Usar verificación alternativa"
                          >
                            <FontAwesomeIcon icon={faShuffle} className="me-2" />
                            Usar verificación alternativa
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="primary"
                          className={isFlowCompleted ? 'flex-fill' : ''}
                          onClick={handleContinue}
                          disabled={!canContinueWithProcessedSteps() || locationValidationFailed}
                        >
                          Continuar
                          <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default CustomerHandoffStep;
